import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { pluralReadableTypeName, readableTypeName } from '@iotv/core-services';
import { DeviceConfig, getNiceDate, isDeviceConfig, isRollingPatternDisabledConfig, isRollingPatternReport, } from '@iotv/datamodel-core';
import { AdjacentType, isNonNaNNumber, objectsMatch } from '@iotv/iotv-v3-types';
import { Chip, chipClasses, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ItemLink } from '../../../../../../../../components/common/ItemLinkList';
import { AppMainViewContext } from '../../../../../../../../ContextProviders/AppContextProvider';
import { NodeEdgeMapperContext } from '../../../../../../../../ContextProviders/NodeEdgeMapperComponentContext';
import { AdminCardWrapper } from '../../../../common/AdminCardWrapper';
import { StatusChip } from '../../../../common/StatusChip';
import { StatusChipsKey } from '../../../../common/StatusChipKey';
import { EntityRollingProgrammeContext } from '../../../Reducers/EntityRollingProgrammeReducer';
/** due for refactor to use src/v2/components/generic/Control/EntityRollingProgamme/fns/schedulerContextMatchesReported.ts
 * which was derived from the original code here
 */
const debug = process.env.REACT_APP_DEBUG && false;
export const ScheduleStatus = () => {
    const [{ ipData }] = useContext(AppMainViewContext);
    const [state, dispatch] = useContext(EntityRollingProgrammeContext);
    const { state: nodeEdgeMapperState } = useContext(NodeEdgeMapperContext);
    const { rollingProgramme, scheduler } = state;
    const scheduledEntities = scheduler?.getAllItems() ?? [];
    const navigate = useNavigate();
    const location = useLocation();
    useEffect(() => {
        //redirect back to WindowAllocation if scheduler not initalized
        if (!scheduler || !scheduler.isInitialized) {
            const { pathname } = location;
            navigate(`${pathname}/?tabName=WindowAllocation`, {});
        }
    }, []);
    const loadContextFromDatastore = async (scheduler) => {
        debug && console.log(`ScheduleStatus used effect calling loadContextFromDatastore`);
        const { err, data: context } = await scheduler.loadContextFromDatastore();
        if (err) {
            console.log('ScheduleStatus loadContextFromDatastore err', err, context);
        }
        if (context) {
            const action = { type: 'Recalculate', value: null };
            dispatch(action);
        }
    };
    useEffect(() => {
        if (scheduler) {
            if (scheduler.isInitialized) {
                debug && console.log(`ScheduleStatus scheduler.isInitialized ${scheduler.isInitialized}`);
            }
            else {
                debug && console.log(`ScheduleStatus used effect calling loadContextFromDatastore`);
                loadContextFromDatastore(scheduler);
            }
        }
    }, [state.schedulerHash]);
    const components = {
        header: (_jsx(Stack, { children: _jsxs(Stack, { direction: 'row', width: '100%', children: [_jsx(Stack, { direction: 'row', width: '50%', children: _jsxs(Typography, { children: [" ", `${rollingProgramme?.name ?? ''} Rolling Schedule`] }) }), _jsx(Stack, { direction: 'row-reverse', width: '50%', justifyContent: 'right', children: _jsx(Typography, { children: `${ipData?.timezone} ${ipData?.utcOffsetDisplay}` }) })] }) })),
        scheduledEntities: (_jsx(Stack, { children: scheduledEntities.length > 0 && (_jsxs(AdminCardWrapper, { children: [_jsxs(Stack, { direction: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 2, children: [_jsxs(Typography, { variant: "h6", children: [`Scheduled ${pluralReadableTypeName(rollingProgramme?.supportedType ?? 'Entities')}`, ":"] }), _jsx(StatusChipsKey, {})] }), _jsx(List, { children: scheduledEntities.map(renderEntityRow) })] })) })),
    };
    function renderEntityRow(entity) {
        const feedbackElements = [];
        if (entity && rollingProgramme && scheduler) {
            /** the configured context as per the UI */
            const schedulerContext = scheduler.context;
            let lastUpdated = null;
            const window = scheduler.getWindowForItem(entity);
            if (window) {
                const thisRollingProgrammeSchedule = rollingProgramme.getRollingProgrammeConfigForWindowIdx(window.windowIdx);
                const devices = schedulerContext.getNodesByPath(entity, [{ adjacencyType: AdjacentType.CHILD, objectTypeId: 'Device' }]);
                if (devices.length === 0) {
                    debug && console.log(`WARN: NO DEVICES!`, schedulerContext.getSummary());
                    const type = readableTypeName(entity.type ?? 'Entity');
                    feedbackElements.push(_jsx(StatusChip, { label: "Schedule", state: 'failed', tooltip: `This ${type} doesn't have a device!` }, "schedule"));
                    feedbackElements.push(_jsx(StatusChip, { label: "Requested", state: 'failed', tooltip: `This ${type} doesn't have a device!` }, "requested"));
                    feedbackElements.push(_jsx(StatusChip, { label: "Reported", state: 'failed', tooltip: `This ${type} doesn't have a device!` }, "reported"));
                }
                if (devices.length === 1) {
                    const currentDeviceConfig = nodeEdgeMapperState.nodeEdgeMapper.getItem(DeviceConfig.createForDevice(devices.at(0)));
                    if (isDeviceConfig(currentDeviceConfig)) {
                        const { reported, requested, metadata, lastReportedMs, lastRequestedMs, lastRequestedBroadcastScheduleSk } = currentDeviceConfig;
                        if (isNonNaNNumber(metadata?.lastUpdatedMs)) {
                            lastUpdated = metadata.lastUpdatedMs;
                        }
                        const isThisDisabledRollingProgramme = isRollingPatternDisabledConfig(thisRollingProgrammeSchedule);
                        const isRequestedDisabledRollingProgramme = isRollingPatternDisabledConfig(requested?.rollingPatternMode);
                        const isReportedDisabledRollingProgramme = isRollingPatternReport(reported?.scheduleReport?.rollingPatternConfig) && reported?.scheduleReport.rollingPatternConfig.enabled === 0;
                        /** broadcastData */
                        const broadcastScheduleMatches = rollingProgramme?.sk === lastRequestedBroadcastScheduleSk;
                        const broadcastTimeNewer = rollingProgramme?.appliedTimestampsMs?.[0] !== undefined && rollingProgramme?.appliedTimestampsMs?.[0] <= (lastUpdated ?? 0);
                        let broadcastState = 'pending';
                        let broadcastTooltip = '';
                        if (broadcastScheduleMatches) {
                            if (broadcastTimeNewer) {
                                broadcastState = 'success';
                                broadcastTooltip = `This is the applied schedule${lastUpdated ? ` (last checked at ${getNiceDate(lastUpdated)})` : ''}`;
                            }
                            else {
                                broadcastState = 'error';
                                broadcastTooltip = `This is the applied schedule${rollingProgramme?.appliedTimestampsMs?.[0] && lastUpdated ? ` (changed at ${getNiceDate(rollingProgramme.appliedTimestampsMs[0])}, last checked at ${getNiceDate(lastUpdated)})` : ''}`;
                            }
                        }
                        else {
                            broadcastState = 'failed';
                            broadcastTooltip = `This is not the applied schedule!`;
                        }
                        feedbackElements.push(_jsx(StatusChip, { label: "Schedule", state: broadcastState, tooltip: broadcastTooltip }, "schedule"));
                        if (requested) {
                            const { rollingPatternMode } = requested;
                            const disabledMatch = isThisDisabledRollingProgramme && isRequestedDisabledRollingProgramme;
                            const requestedMatch = objectsMatch(thisRollingProgrammeSchedule ?? { never: true }, rollingPatternMode);
                            let requestedState = 'pending';
                            let requestedTooltip = '';
                            if (broadcastScheduleMatches) {
                                if (isThisDisabledRollingProgramme) {
                                    if (disabledMatch) {
                                        requestedState = 'success';
                                        requestedTooltip = `This schedule is disabled${lastRequestedMs ? ` (last checked at ${getNiceDate(lastRequestedMs)})` : ''}`;
                                    }
                                    else {
                                        requestedState = 'error';
                                        requestedTooltip = "This schedule is disabled, but hasn't been requested yet!";
                                    }
                                }
                                else {
                                    if (requestedMatch) {
                                        requestedState = 'success';
                                        requestedTooltip = `This does match the requested schedule${lastRequestedMs ? ` (last checked at ${getNiceDate(lastRequestedMs)})` : ''}`;
                                    }
                                    else {
                                        requestedState = 'error';
                                        requestedTooltip = `This doesn't match the requested schedule!${lastRequestedMs ? ` (last checked at ${getNiceDate(lastRequestedMs)})` : ''}`;
                                    }
                                }
                            }
                            else {
                                requestedState = 'failed';
                                requestedTooltip = 'This is not the applied schedule!';
                            }
                            feedbackElements.push(_jsx(StatusChip, { label: "Requested", state: requestedState, tooltip: requestedTooltip }, "requested"));
                        }
                        else {
                            feedbackElements.push(_jsx(StatusChip, { label: "Requested", state: 'failed', tooltip: "This programme hasn't been requested!" }, "requested"));
                        }
                        if (reported) {
                            const { scheduleReport } = reported;
                            if (scheduleReport) {
                                const { rollingPatternConfig } = scheduleReport;
                                const disabledMatch = isRequestedDisabledRollingProgramme && isReportedDisabledRollingProgramme;
                                const reportedMatch = objectsMatch(thisRollingProgrammeSchedule ?? { never: true }, rollingPatternConfig);
                                let reportedState = 'pending';
                                let reportedTooltip = '';
                                if (broadcastScheduleMatches) {
                                    if (isThisDisabledRollingProgramme) {
                                        if (disabledMatch) {
                                            reportedState = 'success';
                                            reportedTooltip = `This schedule is disabled${lastReportedMs ? ` (last checked at ${getNiceDate(lastReportedMs)}))` : ''}`;
                                        }
                                        else {
                                            reportedState = 'error';
                                            reportedTooltip = "This schedule is disabled, but hasn't been reported yet!";
                                        }
                                    }
                                    else {
                                        if (reportedMatch) {
                                            reportedState = 'success';
                                            reportedTooltip = `This does match the reported schedule${lastReportedMs ? ` (last checked at ${getNiceDate(lastReportedMs)})` : ''}`;
                                        }
                                        else {
                                            reportedState = 'error';
                                            reportedTooltip = `This doesn't match the reported schedule!${lastReportedMs ? ` (last checked at ${getNiceDate(lastReportedMs)})` : ''}`;
                                        }
                                    }
                                }
                                else {
                                    reportedState = 'failed';
                                    reportedTooltip = 'This is not the applied schedule!';
                                }
                                feedbackElements.push(_jsx(StatusChip, { label: "Reported", state: reportedState, tooltip: reportedTooltip }, "reported"));
                            }
                            else {
                                feedbackElements.push(_jsx(StatusChip, { label: "Reported", state: 'failed', tooltip: "The device hasn't reported a programme!" }, "reported"));
                            }
                        }
                        else {
                            feedbackElements.push(_jsx(StatusChip, { label: "Reported", state: 'failed', tooltip: "The device hasn't reported a programme!" }, "reported"));
                        }
                        // debug && console.log(`INFO  ScheduleStatus evaluated:`,{ thisS: thisRollingProgrammeSchedule, requested, reported, lastUpdated: lastUpdated?.valueOf() })
                    }
                    else {
                        debug && console.log(`WARN: ScheduleStatus, no deviceConfig for `, { entity, devices });
                    }
                }
                if (devices.length > 1) {
                    console.log(`WARN: more than one device retreived, not supporting this here`);
                }
            }
            else {
                debug && console.log(`WARN: ScheduleStatus, no window for `, { entity });
            }
        }
        else {
            debug && console.log(`WARN: ScheduleStatus, no something`, { entity, rollingProgramme, scheduler });
        }
        return (_jsx(ListItem, { disablePadding: true, children: _jsx(ListItemText, { children: _jsx(Chip, { label: _jsxs(Stack, { direction: 'row', justifyContent: 'space-between', gap: 2, children: [_jsx(ItemLink, { item: entity, underscoreLinks: true }), _jsx(Stack, { direction: 'row', alignItems: 'center', gap: 0.5, children: feedbackElements })] }), sx: {
                        width: 1,
                        justifyContent: 'unset',
                        [`& > .${chipClasses.label}`]: {
                            flexGrow: 1,
                            paddingInlineEnd: 0.6,
                        },
                    } }) }) }, entity.id));
    }
    return (_jsxs(Stack, { children: [components.header, _jsx(Stack, { direction: 'row', children: _jsx(AdminCardWrapper, { children: components.scheduledEntities }) })] }));
};
